import autoDispose from './autoDispose';
import noop from './noop';
import { asLooseUnion } from './types';

declare global {
  interface LockManager {
    request<T>(
      name: string,
      options: LockOptions,
      callback: LockGrantedCallback<T>,
    ): Promise<Awaited<T>>;
  }
}

export interface LockGuard extends Lock, AsyncDisposable {
  /** A signal that aborts when the lock is released */
  signal: AbortSignal;
}

interface AcquireLockOptions {
  mode?: 'exclusive' | 'shared';
  signal?: AbortSignal;
}

interface TryAcquireLockOptions {
  mode?: 'exclusive' | 'shared';
  ifAvailable: boolean;
}

interface StealLockOptions {
  mode?: 'exclusive';
  steal: boolean;
}

export interface AcquireLock {
  (name: string, options?: AcquireLockOptions): Promise<LockGuard>;
  (name: string, options: TryAcquireLockOptions): Promise<LockGuard | null>;
  (name: string, options: StealLockOptions): Promise<LockGuard>;
}

export interface CreateAcquireLockOptions {
  autoRelease?: boolean;
}

export function createAcquireLock(
  lockManager: LockManager,
  { autoRelease = true }: CreateAcquireLockOptions = {},
): AcquireLock {
  return function acquireLock(name, options = {}) {
    const { signal } = asLooseUnion(options);

    let {
      // eslint-disable-next-line prefer-const
      promise: acquirePromise,
      resolve: acquireResolve,
      reject: acquireReject,
    } = Promise.withResolvers<LockGuard>();

    const requestPromise = lockManager.request(name, options, lock => {
      acquireReject = noop;

      if (lock === null) {
        acquireResolve(null!);
        return;
      }

      let {
        // eslint-disable-next-line prefer-const
        promise: releasePromise,
        resolve: releaseResolve,
        reject: releaseReject,
      } = Promise.withResolvers<void>();

      queueMicrotask(() => {
        const releaseAbortController = new AbortController();

        const releaseSignal =
          signal == null
            ? releaseAbortController.signal
            : AbortSignal.any([signal, releaseAbortController.signal]);

        releaseSignal.addEventListener(
          'abort',
          () => {
            releaseReject(releaseSignal.reason);
            releaseReject = noop;
          },
          { once: true },
        );

        // The lock request might be aborted while held,
        // if the lock is stolen or the caller aborts.
        requestPromise.catch(error => {
          releaseAbortController.abort(error);
        });

        // eslint-disable-next-line @studio/no-floating-disposables
        let lockGuard: LockGuard = {
          name: lock.name,
          mode: lock.mode,
          signal: releaseSignal,
          [Symbol.asyncDispose]() {
            releaseResolve();
            releaseResolve = noop;
            return requestPromise;
          },
        };

        if (autoRelease) {
          lockGuard = autoDispose(lockGuard);
        }

        acquireResolve(lockGuard);
        acquireResolve = noop;
      });

      return releasePromise;
    });

    requestPromise.catch(error => {
      acquireReject(error);
    });

    return acquirePromise;
  };
}

if (navigator.locks == null) {
  throw new Error('navigator.locks is not available!');
}

const acquireLock = createAcquireLock(navigator.locks);

export default acquireLock;
